/**
 * Super Admin → Lifetime tab.
 * Foundation-only UI for AppSumo / LTD marketplace administration.
 *
 * NO redemption flow, NO Stripe bypass, NO billing changes. Purely
 * pre-launch infrastructure: marketplace sources, code generation,
 * read-only customers & redemptions tables.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import {
  Plus, Trash2, Loader2, RefreshCw, Download, Upload, Search, ToggleLeft, ToggleRight,
  Ticket, Users, Store, History, Pencil, Power, PowerOff,
} from 'lucide-react';
import { lifetimeAPI } from '../../lib/api';

const TIER_OPTIONS = [
  { value: 'tier_1',    label: 'Tier 1' },
  { value: 'tier_2',    label: 'Tier 2' },
  { value: 'unlimited', label: 'Unlimited' },
];

const STATUS_OPTIONS = [
  { value: '',         label: 'All' },
  { value: 'unused',   label: 'Unused' },
  { value: 'redeemed', label: 'Redeemed' },
  { value: 'refunded', label: 'Refunded' },
  { value: 'disabled', label: 'Disabled' },
];

const STATUS_STYLES = {
  unused:   'bg-slate-100 text-slate-700 border-slate-200',
  redeemed: 'bg-green-50 text-green-700 border-green-200',
  refunded: 'bg-rose-50 text-rose-700 border-rose-200',
  disabled: 'bg-amber-50 text-amber-700 border-amber-200',
};

const fmtDate = (s) => (s ? new Date(s).toLocaleString() : '—');

export default function LifetimeTab() {
  const [innerTab, setInnerTab] = useState('codes');
  const [stats, setStats] = useState(null);

  const reloadStats = useCallback(async () => {
    try {
      const { data } = await lifetimeAPI.stats();
      setStats(data);
    } catch { /* non-fatal */ }
  }, []);

  useEffect(() => { reloadStats(); }, [reloadStats]);

  return (
    <div data-testid="lifetime-tab" className="space-y-4">
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard icon={Ticket} label="Total codes" value={stats.codes_total} testId="stat-codes-total" />
          <StatCard icon={Store} label="Marketplaces" value={stats.sources_total} testId="stat-sources-total" />
          <StatCard icon={Users} label="Customers" value={stats.customers_total} testId="stat-customers-total" />
          <StatCard icon={History} label="Redemptions" value={stats.redemptions_total} testId="stat-redemptions-total" />
        </div>
      )}

      <Tabs value={innerTab} onValueChange={setInnerTab}>
        <div className="-mx-1 overflow-x-auto sm:overflow-visible">
          <TabsList className="w-max sm:w-auto">
            <TabsTrigger value="codes" data-testid="lt-tab-codes">Codes</TabsTrigger>
            <TabsTrigger value="customers" data-testid="lt-tab-customers">Customers</TabsTrigger>
            <TabsTrigger value="redemptions" data-testid="lt-tab-redemptions">Redemptions</TabsTrigger>
            <TabsTrigger value="sources" data-testid="lt-tab-sources">Marketplace Sources</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="codes"><CodesPanel onChange={reloadStats} /></TabsContent>
        <TabsContent value="customers"><CustomersPanel /></TabsContent>
        <TabsContent value="redemptions"><RedemptionsPanel /></TabsContent>
        <TabsContent value="sources"><SourcesPanel onChange={reloadStats} /></TabsContent>
      </Tabs>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, testId }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-orange-50 text-orange-600 flex items-center justify-center">
            <Icon size={18} />
          </div>
          <div className="min-w-0">
            <p className="text-xs text-slate-500">{label}</p>
            <p className="text-xl font-semibold text-slate-900" data-testid={testId}>{value}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Marketplace Sources ───────────────────────────────────────────────────────
function SourcesPanel({ onChange }) {
  const [sources, setSources] = useState([]);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await lifetimeAPI.listSources();
      setSources(Array.isArray(data) ? data : []);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    const n = name.trim();
    if (!n) return toast.error('Name is required');
    setBusy(true);
    try {
      await lifetimeAPI.createSource({ name: n });
      setName('');
      await load();
      onChange?.();
      toast.success('Marketplace source created');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create');
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (s) => {
    try {
      await lifetimeAPI.updateSource(s.id, { active: !s.active });
      await load();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete marketplace "${s.name}"?`)) return;
    try {
      await lifetimeAPI.deleteSource(s.id);
      await load();
      onChange?.();
      toast.success('Deleted');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <Card data-testid="sources-panel">
      <CardContent className="p-4 space-y-4">
        <div className="flex gap-2 items-end flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <Label htmlFor="lt-source-name">Marketplace name</Label>
            <Input
              id="lt-source-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. AppSumo, DealMirror, Dealify, PitchGround, Custom"
              data-testid="lt-source-name-input"
            />
          </div>
          <Button
            type="button"
            onClick={create}
            disabled={busy}
            className="bg-orange-500 hover:bg-orange-600 text-white"
            data-testid="lt-source-create-btn"
          >
            {busy ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Plus size={14} className="mr-1" />}
            Add Source
          </Button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Name</th>
                <th className="text-left px-3 py-2 font-medium">Slug</th>
                <th className="text-left px-3 py-2 font-medium">Active</th>
                <th className="text-left px-3 py-2 font-medium">Created</th>
                <th className="text-right px-3 py-2 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (
                <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-400">Loading…</td></tr>
              )}
              {!loading && sources.length === 0 && (
                <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-400" data-testid="lt-sources-empty">No marketplace sources yet</td></tr>
              )}
              {sources.map((s) => (
                <tr key={s.id} className="hover:bg-slate-50" data-testid={`lt-source-row-${s.id}`}>
                  <td className="px-3 py-2 font-medium text-slate-800">{s.name}</td>
                  <td className="px-3 py-2 text-slate-500 font-mono text-xs">{s.slug}</td>
                  <td className="px-3 py-2">
                    {s.active ? (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5">Active</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 bg-slate-100 rounded-full px-2 py-0.5">Disabled</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-slate-500 text-xs whitespace-nowrap">{fmtDate(s.created_at)}</td>
                  <td className="px-3 py-2 text-right space-x-1">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => toggleActive(s)}
                      data-testid={`lt-source-toggle-${s.id}`}
                    >
                      {s.active ? <ToggleRight size={14} className="mr-1" /> : <ToggleLeft size={14} className="mr-1" />}
                      {s.active ? 'Disable' : 'Enable'}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="text-rose-700 border-rose-200 hover:bg-rose-50"
                      onClick={() => remove(s)}
                      data-testid={`lt-source-delete-${s.id}`}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Codes ────────────────────────────────────────────────────────────────────
function CodesPanel({ onChange }) {
  const [codes, setCodes] = useState([]);
  const [sources, setSources] = useState([]);
  const [filters, setFilters] = useState({ status: '', marketplace_source: '', tier: '', q: '' });
  const [loading, setLoading] = useState(true);
  const [genOpen, setGenOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [generatedPreview, setGeneratedPreview] = useState(null);
  const [editingCode, setEditingCode] = useState(null);
  const [deletingCode, setDeletingCode] = useState(null);
  const [rowBusy, setRowBusy] = useState({}); // { [codeId]: true }
  const [selected, setSelected] = useState(() => new Set()); // Set of code ids
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

  const loadSources = useCallback(async () => {
    try {
      const { data } = await lifetimeAPI.listSources();
      setSources(Array.isArray(data) ? data : []);
    } catch { setSources([]); }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.marketplace_source) params.marketplace_source = filters.marketplace_source;
      if (filters.tier) params.tier = filters.tier;
      if (filters.q) params.q = filters.q;
      const { data } = await lifetimeAPI.listCodes(params);
      setCodes(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load codes');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { loadSources(); }, [loadSources]);
  useEffect(() => { load(); }, [load]);

  const sourceName = (id) => sources.find((s) => s.id === id)?.name || id || '—';

  const downloadCsv = () => {
    const params = {};
    if (filters.status) params.status = filters.status;
    if (filters.marketplace_source) params.marketplace_source = filters.marketplace_source;
    const url = lifetimeAPI.exportCodesUrl(params);
    // Server expects auth header — fetch and download as blob to keep it simple.
    fetch(url, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
    })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement('a');
        const objectUrl = URL.createObjectURL(blob);
        a.href = objectUrl;
        a.download = `lifetime_codes.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(objectUrl);
      })
      .catch(() => toast.error('Export failed'));
  };

  const setBusy = (id, val) => setRowBusy((m) => ({ ...m, [id]: val }));

  const handleToggle = async (code) => {
    if (code.status === 'redeemed') {
      toast.error('Cannot change status of a redeemed code');
      return;
    }
    const next = code.status === 'disabled' ? 'unused' : 'disabled';
    setBusy(code.id, true);
    try {
      const { data } = await lifetimeAPI.updateCodeStatus(code.id, next);
      setCodes((arr) => arr.map((c) => (c.id === code.id ? data : c)));
      toast.success(next === 'disabled' ? 'Code disabled' : 'Code enabled');
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update code');
    } finally {
      setBusy(code.id, false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deletingCode) return;
    setBusy(deletingCode.id, true);
    try {
      await lifetimeAPI.deleteCode(deletingCode.id);
      setCodes((arr) => arr.filter((c) => c.id !== deletingCode.id));
      toast.success('Code deleted');
      setDeletingCode(null);
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    } finally {
      setBusy(deletingCode.id, false);
    }
  };

  const handleEditSaved = (updated) => {
    setCodes((arr) => arr.map((c) => (c.id === updated.id ? updated : c)));
    setEditingCode(null);
  };

  // ── Bulk selection ────────────────────────────────────────────────────────
  // Drop selections whenever the filtered set changes so we never act on
  // rows the admin can't see.
  useEffect(() => {
    setSelected(new Set());
  }, [codes]);

  const allVisibleSelected = codes.length > 0 && codes.every((c) => selected.has(c.id));
  const someVisibleSelected = !allVisibleSelected && codes.some((c) => selected.has(c.id));

  const toggleRow = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAllVisible = (checked) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) codes.forEach((c) => next.add(c.id));
      else codes.forEach((c) => next.delete(c.id));
      return next;
    });
  };

  const selectedCodes = () => codes.filter((c) => selected.has(c.id));

  const summaryToast = (result) => {
    const parts = [];
    const verb =
      result.operation === 'delete' ? 'Deleted' :
      result.operation === 'disable' ? 'Disabled' : 'Enabled';
    parts.push(`${verb} ${result.succeeded}`);
    if (result.skipped_redeemed) parts.push(`skipped ${result.skipped_redeemed} redeemed`);
    if (result.unchanged) parts.push(`${result.unchanged} unchanged`);
    if (result.not_found) parts.push(`${result.not_found} not found`);
    toast.success(parts.join(' · '));
  };

  const runBulk = async (operation) => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    setBulkBusy(true);
    try {
      const { data } = await lifetimeAPI.bulkCodes(operation, ids);
      summaryToast(data);
      if (operation === 'delete' && data.succeeded_ids?.length) {
        const removed = new Set(data.succeeded_ids);
        setCodes((arr) => arr.filter((c) => !removed.has(c.id)));
      } else if (data.succeeded_ids?.length) {
        const newStatus = operation === 'disable' ? 'disabled' : 'unused';
        const changed = new Set(data.succeeded_ids);
        setCodes((arr) => arr.map((c) => (changed.has(c.id) ? { ...c, status: newStatus } : c)));
      }
      setSelected(new Set());
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Bulk ${operation} failed`);
    } finally {
      setBulkBusy(false);
      setBulkDeleteOpen(false);
    }
  };

  const exportSelected = (format) => {
    const rows = selectedCodes();
    if (rows.length === 0) return;
    const d = new Date();
    const p = (n) => String(n).padStart(2, '0');
    const stamp = `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}_${p(d.getHours())}${p(d.getMinutes())}`;
    const trigger = (filename, content, mime) => {
      const blob = new Blob([content], { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
    };
    if (format === 'txt') {
      trigger(`restaff_codes_${stamp}.txt`, rows.map((r) => r.code).join('\n') + '\n', 'text/plain;charset=utf-8');
      toast.success('TXT downloaded');
      return;
    }
    const csvEscape = (v) => {
      const s = v == null ? '' : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const header = ['Code', 'Marketplace', 'Tier', 'Status', 'Created'];
    const body = rows.map((c) => [
      c.code, c.marketplace_source_name || sourceName(c.marketplace_source),
      c.tier || '', c.status || '', c.created_at || '',
    ].map(csvEscape).join(','));
    trigger(`restaff_codes_${stamp}.csv`, [header.join(','), ...body].join('\n') + '\n', 'text/csv;charset=utf-8');
    toast.success('CSV downloaded');
  };

  return (
    <Card data-testid="codes-panel">
      <CardContent className="p-4 space-y-4">
        {/* Filter row */}
        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:items-end">
          <div className="w-full sm:flex-1 sm:min-w-[180px]">
            <Label className="text-xs">Search code</Label>
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={filters.q}
                onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                placeholder="RS-..."
                className="pl-8"
                data-testid="lt-search-input"
              />
            </div>
          </div>
          <div className="w-full sm:w-auto">
            <Label className="text-xs">Status</Label>
            <Select value={filters.status || '__all__'} onValueChange={(v) => setFilters((f) => ({ ...f, status: v === '__all__' ? '' : v }))}>
              <SelectTrigger className="w-full sm:w-[140px]" data-testid="lt-filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All statuses</SelectItem>
                {STATUS_OPTIONS.filter((s) => s.value).map((s) => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="w-full sm:w-auto">
            <Label className="text-xs">Marketplace</Label>
            <Select
              value={filters.marketplace_source || '__all__'}
              onValueChange={(v) => setFilters((f) => ({ ...f, marketplace_source: v === '__all__' ? '' : v }))}
            >
              <SelectTrigger className="w-full sm:w-[180px]" data-testid="lt-filter-source"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All marketplaces</SelectItem>
                {sources.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="w-full sm:w-auto">
            <Label className="text-xs">Tier</Label>
            <Select value={filters.tier || '__all__'} onValueChange={(v) => setFilters((f) => ({ ...f, tier: v === '__all__' ? '' : v }))}>
              <SelectTrigger className="w-full sm:w-[120px]" data-testid="lt-filter-tier"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All tiers</SelectItem>
                {TIER_OPTIONS.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 sm:flex sm:flex-row gap-2 w-full sm:w-auto sm:ml-auto sm:items-end">
            <Button type="button" variant="outline" size="sm" onClick={load} data-testid="lt-codes-refresh">
              <RefreshCw size={14} className="mr-1" /> Refresh
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={downloadCsv} data-testid="lt-codes-export-btn">
              <Download size={14} className="mr-1" /> Export CSV
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => setImportOpen(true)} data-testid="lt-codes-import-btn">
              <Upload size={14} className="mr-1" /> Import CSV
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={() => setGenOpen(true)}
              className="bg-orange-500 hover:bg-orange-600 text-white"
              data-testid="lt-codes-generate-btn"
            >
              <Plus size={14} className="mr-1" /> Generate Codes
            </Button>
          </div>
        </div>

        {/* Bulk toolbar — visible only when at least one row is selected */}
        {selected.size > 0 && (
          <div
            className="flex flex-wrap items-center gap-2 px-3 py-2 rounded-lg border border-orange-200 bg-orange-50"
            data-testid="lt-bulk-toolbar"
          >
            <span className="text-sm text-slate-700 font-medium" data-testid="lt-bulk-count">
              {selected.size} selected
            </span>
            <Button
              type="button" variant="outline" size="sm"
              onClick={() => runBulk('disable')} disabled={bulkBusy}
              data-testid="lt-bulk-disable"
            >
              <PowerOff size={14} className="mr-1" /> Disable Selected
            </Button>
            <Button
              type="button" variant="outline" size="sm"
              onClick={() => runBulk('enable')} disabled={bulkBusy}
              data-testid="lt-bulk-enable"
            >
              <Power size={14} className="mr-1" /> Enable Selected
            </Button>
            <Button
              type="button" variant="outline" size="sm"
              onClick={() => setBulkDeleteOpen(true)} disabled={bulkBusy}
              className="text-red-600 hover:bg-red-50 border-red-200"
              data-testid="lt-bulk-delete"
            >
              <Trash2 size={14} className="mr-1" /> Delete Selected
            </Button>
            <Button
              type="button" variant="outline" size="sm"
              onClick={() => exportSelected('csv')} disabled={bulkBusy}
              data-testid="lt-bulk-export-csv"
            >
              <Download size={14} className="mr-1" /> Export CSV
            </Button>
            <Button
              type="button" variant="outline" size="sm"
              onClick={() => exportSelected('txt')} disabled={bulkBusy}
              data-testid="lt-bulk-export-txt"
            >
              <Download size={14} className="mr-1" /> Export TXT
            </Button>
            <Button
              type="button" variant="ghost" size="sm"
              onClick={() => setSelected(new Set())} disabled={bulkBusy}
              className="ml-auto"
              data-testid="lt-bulk-clear"
            >
              Clear
            </Button>
          </div>
        )}

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="px-3 py-2 w-8">
                  <Checkbox
                    checked={allVisibleSelected ? true : (someVisibleSelected ? 'indeterminate' : false)}
                    onCheckedChange={(v) => toggleAllVisible(!!v)}
                    aria-label="Select all"
                    data-testid="lt-bulk-select-all"
                  />
                </th>
                <th className="text-left px-3 py-2 font-medium">Code</th>
                <th className="text-left px-3 py-2 font-medium">Marketplace</th>
                <th className="text-left px-3 py-2 font-medium">Tier</th>
                <th className="text-left px-3 py-2 font-medium">Status</th>
                <th className="text-left px-3 py-2 font-medium">Redeemed by</th>
                <th className="text-left px-3 py-2 font-medium">Created</th>
                <th className="text-right px-3 py-2 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (
                <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-400">Loading…</td></tr>
              )}
              {!loading && codes.length === 0 && (
                <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-400" data-testid="lt-codes-empty">No codes match the current filters</td></tr>
              )}
              {codes.map((c) => {
                const isRedeemed = c.status === 'redeemed';
                const isDisabled = c.status === 'disabled';
                const busy = !!rowBusy[c.id];
                const checked = selected.has(c.id);
                return (
                  <tr key={c.id} className="hover:bg-slate-50" data-testid={`lt-code-row-${c.id}`}>
                    <td className="px-3 py-2">
                      <Checkbox
                        checked={checked}
                        onCheckedChange={() => toggleRow(c.id)}
                        aria-label={`Select ${c.code}`}
                        data-testid={`lt-code-select-${c.id}`}
                      />
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-slate-800">{c.code}</td>
                    <td className="px-3 py-2 text-slate-600">{c.marketplace_source_name || sourceName(c.marketplace_source)}</td>
                    <td className="px-3 py-2 text-slate-600">{TIER_OPTIONS.find((t) => t.value === c.tier)?.label || c.tier}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center gap-1 text-xs font-medium border rounded-full px-2 py-0.5 ${STATUS_STYLES[c.status] || ''}`}>
                        {c.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500">{c.redeemed_by_email || '—'}</td>
                    <td className="px-3 py-2 text-xs text-slate-500 whitespace-nowrap">{fmtDate(c.created_at)}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          type="button" variant="ghost" size="sm"
                          onClick={() => setEditingCode(c)}
                          disabled={busy || isRedeemed}
                          title={isRedeemed ? 'Redeemed codes cannot be edited' : 'Edit'}
                          data-testid={`lt-code-edit-${c.id}`}
                          className="h-7 px-2"
                        >
                          <Pencil size={14} />
                        </Button>
                        <Button
                          type="button" variant="ghost" size="sm"
                          onClick={() => handleToggle(c)}
                          disabled={busy || isRedeemed}
                          title={isRedeemed ? 'Redeemed codes cannot change status' : (isDisabled ? 'Enable' : 'Disable')}
                          data-testid={`lt-code-toggle-${c.id}`}
                          className="h-7 px-2"
                        >
                          {busy ? <Loader2 size={14} className="animate-spin" /> : (isDisabled ? <Power size={14} className="text-green-600" /> : <PowerOff size={14} className="text-orange-600" />)}
                        </Button>
                        <Button
                          type="button" variant="ghost" size="sm"
                          onClick={() => setDeletingCode(c)}
                          disabled={busy || isRedeemed}
                          title={isRedeemed ? 'Redeemed codes cannot be deleted' : 'Delete'}
                          data-testid={`lt-code-delete-${c.id}`}
                          className="h-7 px-2 text-red-600 hover:bg-red-50"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>

      <EditCodeDialog
        code={editingCode}
        sources={sources}
        onClose={() => setEditingCode(null)}
        onSaved={handleEditSaved}
      />

      <DeleteCodeDialog
        code={deletingCode}
        busy={deletingCode ? !!rowBusy[deletingCode.id] : false}
        onClose={() => setDeletingCode(null)}
        onConfirm={handleConfirmDelete}
      />

      <Dialog open={bulkDeleteOpen} onOpenChange={(v) => !v && !bulkBusy && setBulkDeleteOpen(false)}>
        <DialogContent className="max-w-sm" data-testid="lt-bulk-delete-modal">
          <DialogHeader>
            <DialogTitle>Delete {selected.size} codes permanently?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500">
            Redeemed codes in your selection will be skipped automatically.
            This cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDeleteOpen(false)} disabled={bulkBusy}>Cancel</Button>
            <Button
              onClick={() => runBulk('delete')}
              disabled={bulkBusy}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="lt-bulk-delete-confirm"
            >
              {bulkBusy ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Trash2 size={14} className="mr-1" />}
              Delete {selected.size}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <GenerateCodesDialog
        open={genOpen}
        sources={sources.filter((s) => s.active)}
        onClose={() => setGenOpen(false)}
        onDone={(result) => {
          setGenOpen(false);
          setGeneratedPreview(result);
          load();
          onChange?.();
        }}
      />

      <ImportCodesDialog
        open={importOpen}
        sources={sources}
        onClose={() => setImportOpen(false)}
        onDone={() => {
          setImportOpen(false);
          load();
          onChange?.();
        }}
      />

      <GeneratedPreviewDialog
        open={!!generatedPreview}
        result={generatedPreview}
        onClose={() => setGeneratedPreview(null)}
      />
    </Card>
  );
}

function GenerateCodesDialog({ open, sources, onClose, onDone }) {
  const [marketplaceSource, setMarketplaceSource] = useState('');
  const [tier, setTier] = useState('tier_1');
  const [quantity, setQuantity] = useState(10);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setMarketplaceSource(sources[0]?.id || '');
      setTier('tier_1');
      setQuantity(10);
      setNotes('');
    }
  }, [open, sources]);

  const submit = async () => {
    if (!marketplaceSource) return toast.error('Pick a marketplace');
    if (!quantity || quantity < 1 || quantity > 10000) return toast.error('Quantity 1–10000');
    setBusy(true);
    try {
      const { data } = await lifetimeAPI.generateCodes({
        marketplace_source: marketplaceSource, tier, quantity: Number(quantity), notes: notes.trim() || null,
      });
      toast.success(`Generated ${data.created} codes`);
      onDone?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Generation failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !busy && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="lt-generate-modal">
        <DialogHeader><DialogTitle>Generate Codes</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Marketplace</Label>
            <Select value={marketplaceSource} onValueChange={setMarketplaceSource}>
              <SelectTrigger data-testid="lt-gen-marketplace"><SelectValue placeholder="Select marketplace" /></SelectTrigger>
              <SelectContent>
                {sources.length === 0 && <SelectItem value="__none__" disabled>No active marketplaces — add one first</SelectItem>}
                {sources.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Tier</Label>
            <Select value={tier} onValueChange={setTier}>
              <SelectTrigger data-testid="lt-gen-tier"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TIER_OPTIONS.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Quantity</Label>
            <Input
              type="number"
              min={1}
              max={10000}
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              data-testid="lt-gen-quantity"
            />
          </div>
          <div>
            <Label>Notes <span className="text-slate-400 font-normal">(optional)</span></Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} maxLength={500} data-testid="lt-gen-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="lt-gen-confirm">
            {busy ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Plus size={14} className="mr-1" />}
            Generate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GeneratedPreviewDialog({ open, result, onClose }) {
  if (!result) return null;
  const codes = result.codes || [];
  const codesFull = result.codes_full || codes.map((c) => ({ code: c }));

  const stamp = () => {
    const d = new Date();
    const p = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}_${p(d.getHours())}${p(d.getMinutes())}`;
  };

  const triggerDownload = (filename, content, mime) => {
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const csvEscape = (v) => {
    const s = v == null ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };

  const handleCopy = () => {
    if (!navigator?.clipboard) return;
    navigator.clipboard.writeText(codes.join('\n'));
    toast.success('Codes copied to clipboard');
  };

  const handleDownloadCsv = () => {
    const header = ['Code', 'Marketplace', 'Tier', 'Status', 'Created'];
    const rows = codesFull.map((c) => [
      c.code,
      c.marketplace_source_name || '',
      c.tier || '',
      c.status || 'unused',
      c.created_at || '',
    ].map(csvEscape).join(','));
    const content = [header.join(','), ...rows].join('\n') + '\n';
    triggerDownload(`restaff_codes_${stamp()}.csv`, content, 'text/csv;charset=utf-8');
    toast.success('CSV downloaded');
  };

  const handleDownloadTxt = () => {
    triggerDownload(`restaff_codes_${stamp()}.txt`, codes.join('\n') + '\n', 'text/plain;charset=utf-8');
    toast.success('TXT downloaded');
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="lt-generated-modal">
        <DialogHeader><DialogTitle>Codes generated — {result.created}</DialogTitle></DialogHeader>
        <div className="max-h-72 overflow-y-auto bg-slate-50 border rounded p-2 text-xs font-mono whitespace-pre">
          {codes.join('\n')}
        </div>
        <DialogFooter className="flex flex-col gap-2 sm:flex-row sm:justify-between sm:items-center sm:gap-2 sm:space-x-0">
          <Button
            variant="outline"
            onClick={onClose}
            data-testid="lt-generated-close"
            className="w-full sm:w-auto"
          >
            Close
          </Button>
          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-2">
            <Button
              variant="outline"
              onClick={handleDownloadTxt}
              data-testid="lt-generated-download-txt"
              className="w-full sm:w-auto"
            >
              <Download size={14} className="mr-1" /> TXT
            </Button>
            <Button
              variant="outline"
              onClick={handleDownloadCsv}
              data-testid="lt-generated-download-csv"
              className="w-full sm:w-auto"
            >
              <Download size={14} className="mr-1" /> Download CSV
            </Button>
            <Button
              onClick={handleCopy}
              className="w-full sm:w-auto bg-orange-500 hover:bg-orange-600 text-white"
              data-testid="lt-generated-copy"
            >
              Copy to clipboard
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── CSV import (parses on the client, posts JSON rows) ──────────────────────
function ImportCodesDialog({ open, sources, onClose, onDone }) {
  const [text, setText] = useState('');
  const [defaultSource, setDefaultSource] = useState('');
  const [defaultTier, setDefaultTier] = useState('tier_1');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setText('');
      setDefaultSource(sources[0]?.id || '');
      setDefaultTier('tier_1');
    }
  }, [open, sources]);

  const parsed = useMemo(() => {
    const rows = [];
    const lines = (text || '').split(/\r?\n/).filter((l) => l.trim());
    if (!lines.length) return { rows, error: null };
    // Detect header row
    const first = lines[0].toLowerCase();
    const hasHeader = first.includes('code');
    const start = hasHeader ? 1 : 0;
    for (let i = start; i < lines.length; i++) {
      const parts = lines[i].split(',').map((s) => s.trim());
      const code = parts[0];
      if (!code) continue;
      // Optional columns: marketplace_source (id), tier, notes
      const source = parts[1] || defaultSource;
      const tier = parts[2] || defaultTier;
      const notes = parts[3] || null;
      rows.push({ code, marketplace_source: source, tier, notes });
    }
    return { rows, error: null };
  }, [text, defaultSource, defaultTier]);

  const submit = async () => {
    if (!parsed.rows.length) return toast.error('No rows parsed');
    if (!defaultSource && parsed.rows.some((r) => !r.marketplace_source)) {
      return toast.error('Select a default marketplace');
    }
    setBusy(true);
    try {
      const { data } = await lifetimeAPI.importCodes(parsed.rows);
      toast.success(`Imported ${data.created}. Skipped ${data.skipped}.`);
      onDone?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Import failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !busy && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid="lt-import-modal">
        <DialogHeader><DialogTitle>Import Codes (CSV)</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-slate-500">
            Paste CSV rows. Columns: <span className="font-mono">code,marketplace_source_id,tier,notes</span>.
            If a row omits marketplace/tier, the defaults below are used.
          </p>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Default marketplace</Label>
              <Select value={defaultSource} onValueChange={setDefaultSource}>
                <SelectTrigger data-testid="lt-import-default-source"><SelectValue placeholder="Pick one" /></SelectTrigger>
                <SelectContent>
                  {sources.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}{s.active ? '' : ' (disabled)'}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Default tier</Label>
              <Select value={defaultTier} onValueChange={setDefaultTier}>
                <SelectTrigger data-testid="lt-import-default-tier"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TIER_OPTIONS.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <Textarea
            rows={8}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={`RS-APP-T1-AAAA\nRS-APP-T1-BBBB`}
            className="font-mono text-xs"
            data-testid="lt-import-textarea"
          />
          <p className="text-xs text-slate-500" data-testid="lt-import-parsed-count">
            {parsed.rows.length} row{parsed.rows.length === 1 ? '' : 's'} parsed
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !parsed.rows.length} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="lt-import-confirm">
            {busy ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Upload size={14} className="mr-1" />}
            Import
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Lifetime API client helpers (Phase 4 admin actions) ─────────────────
// Inline calls — kept here to avoid a parallel api.js section.
const lifetimeAdmin = {
  disable: (id, payload) => lifetimeAPI ? null : null,
};

// ── Customers (read + Phase 4 actions) ───────────────────────────────────────
function CustomersPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionModal, setActionModal] = useState({ open: false, mode: null, row: null });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await lifetimeAPI.customers();
      setRows(Array.isArray(data) ? data : []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <Card data-testid="customers-panel">
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Tenant</th>
                <th className="text-left px-3 py-2 font-medium">Email</th>
                <th className="text-left px-3 py-2 font-medium">Marketplace</th>
                <th className="text-left px-3 py-2 font-medium">Plan</th>
                <th className="text-left px-3 py-2 font-medium">Redeemed</th>
                <th className="text-left px-3 py-2 font-medium">Refund deadline</th>
                <th className="text-left px-3 py-2 font-medium">Status</th>
                <th className="text-right px-3 py-2 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (<tr><td colSpan={8} className="px-3 py-8 text-center text-slate-400">Loading…</td></tr>)}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-400" data-testid="customers-empty">No lifetime customers yet</td></tr>
              )}
              {rows.map((r) => {
                const isDisabled = r.enabled === false || ['disabled', 'refunded'].includes(r.status);
                return (
                  <tr key={r.tenant_id} className="hover:bg-slate-50" data-testid={`lt-customer-row-${r.tenant_id}`}>
                    <td className="px-3 py-2 text-slate-800 font-medium">{r.tenant_name || r.tenant_id}</td>
                    <td className="px-3 py-2 text-slate-600">{r.email || '—'}</td>
                    <td className="px-3 py-2 text-slate-600">{r.source || '—'}</td>
                    <td className="px-3 py-2 text-slate-600">{r.plan || r.tier || '—'}</td>
                    <td className="px-3 py-2 text-xs text-slate-500 whitespace-nowrap">{fmtDate(r.redeemed_at)}</td>
                    <td className="px-3 py-2 text-xs text-slate-500 whitespace-nowrap">{fmtDate(r.refund_deadline)}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center gap-1 text-xs font-medium border rounded-full px-2 py-0.5 ${STATUS_STYLES[r.status] || ''}`}>
                        {r.status || '—'}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right space-x-1">
                      {!isDisabled && (
                        <Button
                          type="button" size="sm" variant="outline"
                          onClick={() => setActionModal({ open: true, mode: 'disable', row: r })}
                          data-testid={`lt-customer-disable-${r.tenant_id}`}
                          className="text-amber-700 border-amber-200 hover:bg-amber-50 h-7 text-xs"
                        >
                          Disable
                        </Button>
                      )}
                      {isDisabled && (
                        <Button
                          type="button" size="sm" variant="outline"
                          onClick={() => setActionModal({ open: true, mode: 'restore', row: r })}
                          data-testid={`lt-customer-restore-${r.tenant_id}`}
                          className="text-green-700 border-green-200 hover:bg-green-50 h-7 text-xs"
                        >
                          Restore
                        </Button>
                      )}
                      <Button
                        type="button" size="sm" variant="outline"
                        onClick={() => setActionModal({ open: true, mode: 'delete', row: r })}
                        data-testid={`lt-customer-delete-${r.tenant_id}`}
                        className="text-rose-700 border-rose-200 hover:bg-rose-50 h-7 text-xs"
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>

      <CustomerActionDialog
        open={actionModal.open}
        mode={actionModal.mode}
        row={actionModal.row}
        onClose={() => setActionModal({ open: false, mode: null, row: null })}
        onDone={() => { setActionModal({ open: false, mode: null, row: null }); load(); }}
      />
    </Card>
  );
}

const ADMIN_REASONS = [
  { value: 'refund',              label: 'Refund' },
  { value: 'chargeback',          label: 'Chargeback' },
  { value: 'fraud',               label: 'Fraud' },
  { value: 'marketplace_refund',  label: 'Marketplace Refund' },
  { value: 'manual',              label: 'Manual' },
  { value: 'abuse',               label: 'Abuse' },
];

function CustomerActionDialog({ open, mode, row, onClose, onDone }) {
  const [reason, setReason] = useState('manual');
  const [note, setNote] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setReason(mode === 'restore' ? 'manual' : 'refund');
      setNote('');
      setConfirmText('');
    }
  }, [open, mode]);

  if (!row || !mode) return null;
  const title = mode === 'disable' ? 'Disable Lifetime License'
              : mode === 'restore' ? 'Restore Lifetime License'
              : 'Delete Lifetime Tenant';

  const handleSubmit = async () => {
    if (mode === 'delete' && confirmText.trim().toUpperCase() !== 'DELETE') {
      toast.error('Type DELETE to confirm');
      return;
    }
    setBusy(true);
    try {
      const apiBase = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
      const url =
        mode === 'disable' ? `/lifetime/customers/${row.tenant_id}/disable` :
        mode === 'restore' ? `/lifetime/customers/${row.tenant_id}/restore` :
                             `/lifetime/customers/${row.tenant_id}/delete`;
      const body = mode === 'delete'
        ? { reason, note: note.trim() || null, confirm: true }
        : { reason, note: note.trim() || null };
      const res = await fetch(apiBase + url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
        },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data.detail || `${title} failed`);
        return;
      }
      toast.success(
        mode === 'disable' ? 'License disabled' :
        mode === 'restore' ? 'License restored' :
        'Tenant deleted'
      );
      onDone?.();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !busy && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="lt-customer-action-modal">
        <DialogHeader>
          <DialogTitle data-testid="lt-customer-action-title">{title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-slate-500">
            {row.tenant_name || row.tenant_id} · {row.email}
          </p>
          <div>
            <Label className="text-xs">Reason</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger data-testid="lt-customer-action-reason"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ADMIN_REASONS.map((r) => (
                  <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Note <span className="text-slate-400 font-normal">(optional)</span></Label>
            <Textarea
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Optional context for the audit trail"
              maxLength={2000}
              data-testid="lt-customer-action-note"
            />
          </div>
          {mode === 'delete' && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 space-y-2">
              <p className="text-xs text-rose-800 font-medium">
                This action is permanent. Tenant, license, and redemption history will be deleted. An audit note remains.
              </p>
              <Label className="text-xs">Type <span className="font-mono">DELETE</span> to confirm</Label>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="DELETE"
                data-testid="lt-customer-delete-confirm-input"
                className="bg-white"
              />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            disabled={busy}
            className={`text-white ${
              mode === 'restore' ? 'bg-green-600 hover:bg-green-700'
              : mode === 'delete' ? 'bg-rose-600 hover:bg-rose-700'
              : 'bg-amber-600 hover:bg-amber-700'
            }`}
            data-testid="lt-customer-action-confirm"
          >
            {busy ? <Loader2 size={14} className="mr-1 animate-spin" /> : null}
            {mode === 'disable' ? 'Disable' : mode === 'restore' ? 'Restore' : 'Delete tenant'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Redemptions (read-only) ──────────────────────────────────────────────────
function RedemptionsPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    lifetimeAPI.redemptions()
      .then(({ data }) => setRows(Array.isArray(data) ? data : []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);
  return (
    <Card data-testid="redemptions-panel">
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Code</th>
                <th className="text-left px-3 py-2 font-medium">Marketplace</th>
                <th className="text-left px-3 py-2 font-medium">Tenant</th>
                <th className="text-left px-3 py-2 font-medium">Redeemed</th>
                <th className="text-left px-3 py-2 font-medium">IP</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (<tr><td colSpan={5} className="px-3 py-8 text-center text-slate-400">Loading…</td></tr>)}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-400" data-testid="redemptions-empty">No redemptions yet</td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.code} className="hover:bg-slate-50">
                  <td className="px-3 py-2 font-mono text-xs text-slate-800">{r.code}</td>
                  <td className="px-3 py-2 text-slate-600">{r.marketplace || '—'}</td>
                  <td className="px-3 py-2 text-slate-600">{r.tenant_id || '—'}</td>
                  <td className="px-3 py-2 text-xs text-slate-500 whitespace-nowrap">{fmtDate(r.redeemed_at)}</td>
                  <td className="px-3 py-2 text-xs text-slate-500">{r.redeemed_ip || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}


// (Phase 5 "Upgrade Interest" tracking removed — Phase 6 ships real Stripe checkout.)


// ── Edit + Delete dialogs (per-code-row actions) ────────────────────────────

function EditCodeDialog({ code, sources, onClose, onSaved }) {
  const [marketplace, setMarketplace] = useState('');
  const [tier, setTier] = useState('tier_1');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (code) {
      setMarketplace(code.marketplace_source || '');
      setTier(code.tier || 'tier_1');
      setNotes(code.notes || '');
    }
  }, [code]);

  if (!code) return null;

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await lifetimeAPI.editCode(code.id, {
        marketplace_source: marketplace || null,
        tier,
        notes,
      });
      toast.success('Code updated');
      onSaved?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!code} onOpenChange={(v) => !v && !busy && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="lt-edit-code-modal">
        <DialogHeader>
          <DialogTitle>Edit code <span className="font-mono text-sm">{code.code}</span></DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Marketplace</Label>
            <Select value={marketplace} onValueChange={setMarketplace}>
              <SelectTrigger data-testid="lt-edit-marketplace"><SelectValue placeholder="Select marketplace" /></SelectTrigger>
              <SelectContent>
                {sources.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Tier</Label>
            <Select value={tier} onValueChange={setTier}>
              <SelectTrigger data-testid="lt-edit-tier"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TIER_OPTIONS.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Notes <span className="text-slate-400 font-normal">(optional)</span></Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} maxLength={500} data-testid="lt-edit-notes" />
          </div>
          <p className="text-xs text-slate-500">
            The code value itself can’t be changed — it’s the identifier used for redemption.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="lt-edit-save">
            {busy ? <Loader2 size={14} className="mr-1 animate-spin" /> : null}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteCodeDialog({ code, busy, onClose, onConfirm }) {
  if (!code) return null;
  return (
    <Dialog open={!!code} onOpenChange={(v) => !v && !busy && onClose?.()}>
      <DialogContent className="max-w-sm" data-testid="lt-delete-code-modal">
        <DialogHeader><DialogTitle>Delete this code permanently?</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <p className="text-sm text-slate-700">
            <span className="font-mono">{code.code}</span> ({code.tier})
          </p>
          <p className="text-sm text-slate-500">
            This cannot be undone. Redeemed codes cannot be deleted — disable them instead.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button
            onClick={onConfirm}
            disabled={busy}
            className="bg-red-600 hover:bg-red-700 text-white"
            data-testid="lt-delete-confirm"
          >
            {busy ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Trash2 size={14} className="mr-1" />}
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
